import { Form, Formik } from "formik";
import React from "react";
import { settingsUpdate } from "../../../api/api";
import useApi from "../../../hooks/useApi";
import { useModelPrices } from "../../../state/api/ai/useModelPrices";
import { useModels } from "../../../state/api/ai/useModels";
import { useSettings } from "../../../state/api/settings/useSettings";
import { Doc, Model, ModelPrice, Settings } from "../../../types";
import TokenCounter from "../ai/tokens/TokenCounter";
import Submit from "../buttons/Submit";
import FieldWithLabel from "../formik/FieldWithLabel";
import LoadingSpinner from "../loader/LoadingSpinner";
import PageLoader from "../loader/PageLoader";


export default function GeneralSettings() {
  const { settings, loadingSettings, reloadSettings } = useSettings();
  const callApi = useApi();

  if (loadingSettings) return <PageLoader />;

  return (
    <Formik
      initialValues={{
        maxInputTokensPerMonth: settings?.maxInputTokensPerMonth ?? 0,
        maxOutputTokensPerMonth: settings?.maxOutputTokensPerMonth ?? 0
      } satisfies Settings}
      onSubmit={async (values) => {
        const res = await callApi(settingsUpdate(values));
        if (!res?.success) return;
        await reloadSettings();
      }}
    >
      {formik => (
        <Form className="d-flex flex-column gap-3 p-3 overflow-y-auto" style={{ flex: "0 1 auto" }}>
          <div className="row g-3">
            <div className="col-md-6">
              <FieldWithLabel
                type="number"
                name="maxInputTokensPerMonth"
                label="Max. Input Tokens / Month / Customer"
              />
              <div className="mt-1">
                <TokenCounter tokenCount={formik.values.maxInputTokensPerMonth} />
              </div>
            </div>
            <div className="col-md-6">
              <FieldWithLabel
                type="number"
                name="maxOutputTokensPerMonth"
                label="Max. Output Tokens / Month / Customer"
              />
              <div className="mt-1">
                <TokenCounter tokenCount={formik.values.maxOutputTokensPerMonth} />
              </div>
            </div>
          </div>

          <TokenUsageBreakdown
            maxIn={formik.values.maxInputTokensPerMonth}
            maxOut={formik.values.maxOutputTokensPerMonth}
          />

          <Submit />

          <CombinedPricingCalculator
            maxInputTokensPerMonth={formik.values.maxInputTokensPerMonth}
            maxOutputTokensPerMonth={formik.values.maxOutputTokensPerMonth}
          />
        </Form>
      )}
    </Formik>
  );
}


interface CalculatorProps {
  maxInputTokensPerMonth: number;
  maxOutputTokensPerMonth: number;
}

/** A configured model next to the price tier it is billed at, or none if that tier was deleted. */
interface PricedModel {
  model: Doc<Model>;
  price?: Doc<ModelPrice>;
}

const BENCHMARKS = [
  { key: "sheet", label: "1 Sheet (150 rows x 15 cols)", in: 100000, out: 0 },
  { key: "msg", label: "Regular chat message", in: 1000, out: 1000 },
  { key: "analysis", label: "Deal chat / Analysis", in: 450000, out: 10000, main: true }
];


const CombinedPricingCalculator: React.FC<CalculatorProps> = ({
  maxInputTokensPerMonth,
  maxOutputTokensPerMonth
}) => {
  const { models, loadingModels } = useModels();
  const { modelPricesById, loadingModelPrices } = useModelPrices();

  const inputMillions = maxInputTokensPerMonth / 1_000_000;
  const outputMillions = maxOutputTokensPerMonth / 1_000_000;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const calculateActionCost = (inputRate: number, outputRate: number, benchmarkKey: string) => {
    const b = BENCHMARKS.find(x => x.key === benchmarkKey)!;
    return ((b.in / 1_000_000) * inputRate) + ((b.out / 1_000_000) * outputRate);
  };

  if (loadingModels || loadingModelPrices) {
    return <LoadingSpinner size={32} color="var(--primaryAccent)" />;
  }

  const rows: PricedModel[] = models.map(model => ({
    model,
    price: modelPricesById[model.price]
  }));

  if (!rows.length) {
    return (
      <span className="text-muted small">
        No models configured yet. Add one under the Models tab to preview what it costs at these limits.
      </span>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-striped table-hover align-middle small text-dark">
        <thead>
          <tr>
            <th scope="col">Model</th>
            <th scope="col" className="text-end">Max Input $</th>
            <th scope="col" className="text-end">Max Output $</th>
            <th scope="col" className="text-end">Total Max $ / Month</th>
            <th scope="col" className="text-end">$ / Sheet</th>
            <th scope="col" className="text-end">$ / Msg</th>
            <th scope="col" className="text-end">$ / Deal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ model, price }) => {
            const name = model.label || model.externalModelId;

            if (!price) {
              return (
                <tr key={model._id}>
                  <td className="font-monospace fw-bold">{name}</td>
                  <td className="text-muted" colSpan={6}>Price missing</td>
                </tr>
              );
            }

            const inputCost = inputMillions * price.ppmInputTokens;
            const outputCost = outputMillions * price.ppmOutputTokens;
            const totalMonthlyCost = inputCost + outputCost;

            return (
              <tr key={model._id}>
                <td className="font-monospace">
                  <div className="fw-bold">{name}</div>
                  {
                    !!price.label && <div className="text-muted">{price.label}</div>
                  }
                </td>
                <td className="font-monospace text-end">{formatCurrency(inputCost)}</td>
                <td className="font-monospace text-end">{formatCurrency(outputCost)}</td>
                <td className="font-monospace text-end fw-bold">{formatCurrency(totalMonthlyCost)}</td>
                <td className="font-monospace text-end">{formatCurrency(calculateActionCost(price.ppmInputTokens, price.ppmOutputTokens, "sheet"))}</td>
                <td className="font-monospace text-end">{formatCurrency(calculateActionCost(price.ppmInputTokens, price.ppmOutputTokens, "msg"))}</td>
                <td className="font-monospace text-end">{formatCurrency(calculateActionCost(price.ppmInputTokens, price.ppmOutputTokens, "analysis"))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function TokenUsageBreakdown({ maxIn = 0, maxOut = 0 }) {
  const main = BENCHMARKS.find(b => b.main)!;
  const countIn = main.in ? Math.floor(maxIn / main.in) : Infinity;
  const countOut = main.out ? Math.floor(maxOut / main.out) : Infinity;

  const limit = Math.min(countIn, countOut);
  const pool = countIn < countOut ? "Input" : "Output";

  return (
    <div className="d-flex flex-column gap-1 text-muted small card p-2">
      <strong>Deals included: {limit === Infinity ? 0 : limit} (limited by {pool} Pool)</strong>
      {BENCHMARKS.map((b, i) => (
        <span key={i}>
          {b.label} = {b.in ? `~${b.in.toLocaleString()} INPUT` : ""}{b.in && b.out ? " / " : ""}{b.out ? `~${b.out.toLocaleString()} OUTPUT` : ""} tokens
        </span>
      ))}
    </div>
  );
}
